import { useState } from 'react';
import { Button } from './ui/button';
import { AlertTriangle, Plus, X } from 'lucide-react';
import { useLanguage } from '@/contexts/AppContext';

interface SavedCard {
  id: string;
  type: 'visa' | 'mastercard';
  lastFour: string;
  holder: string;
}

interface PaymentErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRetry: (cardId: string) => void;
  onAddNewCard: () => void;
  savedCards: SavedCard[];
  errorMessage?: string;
}

const translations = {
  ko: {
    paymentFailed: '결제 실패',
    errorOccurred: '결제 처리 중 오류가 발생했습니다',
    selectDifferentCard: '다른 카드를 선택하거나 새 카드를 등록해주세요',
    selectCard: '카드 선택',
    addNewCard: '새 카드 등록',
    tryAgain: '다시 시도',
    cancel: '취소',
    commonErrors: {
      declined: '카드사에서 결제를 거절했습니다',
      insufficient: '잔액이 부족합니다',
      expired: '카드가 만료되었습니다',
      invalid: '카드 정보가 올바르지 않습니다'
    }
  },
  es: {
    paymentFailed: 'Pago fallido',
    errorOccurred: 'Ocurrió un error al procesar el pago',
    selectDifferentCard: 'Seleccione una tarjeta diferente o registre una nueva',
    selectCard: 'Seleccionar tarjeta',
    addNewCard: 'Registrar nueva tarjeta',
    tryAgain: 'Intentar de nuevo',
    cancel: 'Cancelar',
    commonErrors: {
      declined: 'El banco rechazó la transacción',
      insufficient: 'Fondos insuficientes',
      expired: 'La tarjeta ha expirado',
      invalid: 'Información de tarjeta inválida'
    }
  },
  en: {
    paymentFailed: 'Payment Failed',
    errorOccurred: 'An error occurred while processing payment',
    selectDifferentCard: 'Please select a different card or add a new one',
    selectCard: 'Select card',
    addNewCard: 'Add new card',
    tryAgain: 'Try again',
    cancel: 'Cancel',
    commonErrors: {
      declined: 'Transaction declined by bank',
      insufficient: 'Insufficient funds',
      expired: 'Card has expired',
      invalid: 'Invalid card information'
    }
  }
};

export function PaymentErrorModal({
  isOpen,
  onClose,
  onRetry,
  onAddNewCard,
  savedCards,
  errorMessage,
}: PaymentErrorModalProps) {
  const { language } = useLanguage();
  const [selectedCard, setSelectedCard] = useState<string>('');
  const t = translations[language];

  if (!isOpen) return null;

  const getErrorMessage = () => {
    if (errorMessage) return errorMessage;
    
    // Random error simulation
    const errors = Object.values(t.commonErrors);
    return errors[Math.floor(Math.random() * errors.length)];
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md mx-auto shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{t.paymentFailed}</h2>
                <p className="text-sm text-gray-600">{t.errorOccurred}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Error Message */}
        <div className="p-6 border-b border-gray-100">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">{getErrorMessage()}</p>
          </div>
          <p className="text-sm text-gray-600 mt-3">{t.selectDifferentCard}</p>
        </div>

        {/* Card Selection */}
        <div className="p-6">
          <h3 className="font-medium text-gray-900 mb-4">{t.selectCard}</h3>
          
          <div className="space-y-3 mb-6">
            {savedCards.map((card) => (
              <button
                key={card.id}
                onClick={() => setSelectedCard(card.id)}
                className={`w-full p-4 rounded-xl border-2 transition-all ${
                  selectedCard === card.id
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-6 rounded flex items-center justify-center ${
                      card.type === 'visa' ? 'bg-blue-600' : 'bg-red-500'
                    }`}>
                      <span className="text-xs font-bold text-white">
                        {card.type.toUpperCase()}
                      </span>
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-sm">{card.type.toUpperCase()}</p>
                      <p className="text-xs text-gray-500">{card.holder}</p>
                    </div>
                  </div>
                  <p className="font-mono text-sm">•••• {card.lastFour}</p>
                </div>
              </button>
            ))}

            {/* Add New Card Option */}
            <button
              onClick={onAddNewCard}
              className="w-full p-4 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center gap-2 text-gray-600 hover:border-primary hover:text-primary transition-all"
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm font-medium">{t.addNewCard}</span>
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              {t.cancel}
            </Button>
            <Button
              onClick={() => selectedCard && onRetry(selectedCard)}
              disabled={!selectedCard}
              className="flex-1 bg-primary hover:bg-primary-hover text-white disabled:bg-gray-300"
            >
              {t.tryAgain}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}